// Phase 1.5 item #2 · Shared UI for the 4 observability tabs
//
// Tiny presentational atoms: empty state, error state, loading row,
// table header/row, state pill. Keeps the 4 page modules consistent
// and free of duplicate boilerplate.
import React from "react";
import { COLORS } from "../../../theme/colors";
import { Button } from "../../../components/ui";

export function EmptyState({ title, detail, action }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "60px 24px", gap: 8,
      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: 6, color: COLORS.textMuted,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{title}</div>
      {detail && (
        <div style={{ fontSize: 12, maxWidth: 460, textAlign: "center", lineHeight: 1.5 }}>
          {detail}
        </div>
      )}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "40px 24px", gap: 10,
      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: 6,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626" }}>⚠ {message}</div>
      {onRetry && (
        <Button onClick={onRetry} variant="secondary" size="sm">Retry</Button>
      )}
    </div>
  );
}

export function LoadingRow({ label = "Loading…" }) {
  return (
    <div style={{
      padding: "40px 24px", textAlign: "center",
      color: COLORS.textMuted, fontSize: 12,
    }}>{label}</div>
  );
}

export function TableHeader({ cols }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${cols.length}, 1fr)`,
      gap: 8, padding: "8px 14px",
      fontSize: 10, fontWeight: 700, color: COLORS.textDim,
      textTransform: "uppercase", letterSpacing: "0.05em",
      borderBottom: `1px solid ${COLORS.border}`,
    }}>
      {cols.map((c, i) => <div key={i}>{c}</div>)}
    </div>
  );
}

export function TableRow({ cells, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cells.length}, 1fr)`,
        gap: 8, padding: "12px 14px", alignItems: "center",
        cursor: onClick ? "pointer" : "default",
        borderBottom: `1px solid ${COLORS.border}40`,
        transition: "background 0.15s",
      }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background = COLORS.surfaceHover || COLORS.surface)}
      onMouseLeave={e => onClick && (e.currentTarget.style.background = "transparent")}
    >
      {cells.map((c, i) => <div key={i}>{c}</div>)}
    </div>
  );
}

const STATE_COLORS = {
  succeeded: { bg: "#dcfce7", fg: "#166534" },
  completed: { bg: "#dcfce7", fg: "#166534" },
  succeed:   { bg: "#dcfce7", fg: "#166534" },
  ok:        { bg: "#dcfce7", fg: "#166534" },
  ACCURATE:  { bg: "#dcfce7", fg: "#166534" },
  pending:   { bg: "#fef3c7", fg: "#92400e" },
  running:   { bg: "#dbeafe", fg: "#1e40af" },
  in_progress: { bg: "#dbeafe", fg: "#1e40af" },
  PARTIAL:   { bg: "#fef3c7", fg: "#92400e" },
  PARTIALLY_ACCURATE: { bg: "#fef3c7", fg: "#92400e" },
  failed:    { bg: "#fee2e2", fg: "#991b1b" },
  INACCURATE:{ bg: "#fee2e2", fg: "#991b1b" },
  error:     { bg: "#fee2e2", fg: "#991b1b" },
};

export function StatePill({ state }) {
  if (!state) {
    return <span style={{ fontSize: 10, color: COLORS.textMuted }}>—</span>;
  }
  const c = STATE_COLORS[state] || { bg: COLORS.surface, fg: COLORS.textMuted };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px", borderRadius: 999,
      fontSize: 10, fontWeight: 600,
      background: c.bg, color: c.fg,
    }}>{String(state)}</span>
  );
}
